import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LinkOverlay } from '@sorare/core/src/atoms/navigation/Box';
import { HeadlineM, LabelM } from '@sorare/core/src/atoms/typography';
import { Box, LabelPosition } from '@sorare/core/src/atoms/ui/Box';
import { ANY_SPORT_SCOUTING_CENTER } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { scoutingCenter } from '@sorare/core/src/lib/glossary';
import { generateSportPath } from '@sorare/core/src/lib/routing/generateSportPath';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import scout from './assets/scout.png';

const NoPaddingBox = styled(Box)`
  --light-green: #bff6d6;
  --dark-green: #048a3d;

  height: 100%;
  position: relative;
  padding: 0;
  overflow: hidden;
  border: 2px solid transparent;
  background:
    linear-gradient(to right, black, black) padding-box,
    linear-gradient(150deg, var(--light-green), var(--dark-green)) border-box;
`;
const Overlay = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;

  opacity: 0;
  background: linear-gradient(
    to bottom,
    transparent 80%,
    var(--dark-green) 150%
  );

  ${NoPaddingBox}:hover & {
    opacity: 1;
  }
  transition: opacity 0.2s ease-in-out;
`;
const Background = styled(Vertical)`
  height: 100%;
  justify-content: flex-end;
  --tile-size: 44px;
  --offset: calc(var(--tile-size) / 2);
  background: linear-gradient(to bottom, transparent, var(--c-black) 80%),
    repeating-linear-gradient(
      to bottom,
      var(--dark-green) var(--offset),
      transparent calc(var(--offset) + 1px),
      transparent calc(var(--offset) + var(--tile-size))
    ),
    repeating-linear-gradient(
      to right,
      var(--dark-green) var(--offset),
      transparent calc(var(--offset) + 1px),
      transparent calc(var(--offset) + var(--tile-size))
    ),
    linear-gradient(to bottom, #3ddc8435, #3ddc8400);

  gap: 0;
  padding: var(--double-unit);
  @media ${tabletAndAbove} {
    flex-direction: column;
    gap: var(--quadruple-unit);
  }
`;
const Wrapper = styled.div`
  opacity: 1;
  position: relative;
  transition: opacity 0.05s ease-in-out;

  &:hover {
    opacity: 0.8;
  }
`;

const CTA = styled(Button)`
  margin-left: auto;
`;

type Props = {
  compact?: boolean;
};

const DefaultVariant = () => {
  const { sport } = useSportContext();

  return (
    <ButtonBase
      to={generateSportPath(ANY_SPORT_SCOUTING_CENTER, {
        sport,
      })}
    >
      <NoPaddingBox
        labelProps={{
          color: 'var(--c-black)',
          bgColor: '#bff6d6',
          position: LabelPosition.LEFT,
          value: (
            <LabelM uppercase bold>
              <FormattedMessage {...scoutingCenter.scout} />
            </LabelM>
          ),
        }}
      >
        <Overlay />
        <Background>
          <Vertical gap={0.5}>
            <HeadlineM>
              <FormattedMessage
                id="ScoutingCenterTile.title"
                defaultMessage="Scouting Center"
              />
            </HeadlineM>
          </Vertical>
        </Background>
      </NoPaddingBox>
    </ButtonBase>
  );
};

const CompactVariant = () => {
  const { sport } = useSportContext();

  return (
    <Wrapper>
      <LinkOverlay
        as={Link}
        to={generateSportPath(ANY_SPORT_SCOUTING_CENTER, {
          sport,
        })}
      >
        <Horizontal>
          <img width={48} height={48} src={scout} alt="" />
          <Vertical>
            <LabelM bold>
              <FormattedMessage {...scoutingCenter.findTheBestPlayers} />
            </LabelM>
            <LabelM color="var(--c-nd-600)">
              <FormattedMessage
                id="ForYou.ScoutingCenter.subtitle"
                defaultMessage="Scouting Center"
              />
            </LabelM>
          </Vertical>
          <CTA size="compact" color="quaternary">
            <FormattedMessage
              id="ForYou.ScoutingCenter.CTA.now"
              defaultMessage="Scout Now"
            />
          </CTA>
        </Horizontal>
      </LinkOverlay>
    </Wrapper>
  );
};

export const ScoutingCenterTile = ({ compact }: Props) => {
  if (compact) {
    return <CompactVariant />;
  }

  return <DefaultVariant />;
};
